/**
 * A small picture for each skill.
 *
 * Drawn as lines rather than loaded, so they are sharp at any size and arrive
 * with the page, and each in a colour of its own: twenty names in a grid are
 * read one at a time, twenty pictures in twenty colours are found at a glance.
 */

/** What each one is drawn from, on a 24 by 24 square, and its colour. */
const ICONS: Readonly<Record<string, { colour: string; parts: JSX.Element }>> = {
  attack: {
    colour: '#e0625c',
    parts: (
      <>
        <path d="M19 5 9.8 14.2" />
        <path d="M19 5h-3.5M19 5v3.5" />
        <path d="M7.2 11.6l5.2 5.2" />
        <path d="M9.8 14.2 5.2 18.8" />
        <circle cx="4.6" cy="19.4" r="1" />
      </>
    ),
  },
  defence: {
    colour: '#5b8def',
    parts: <path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" />,
  },
  strength: {
    colour: '#4caf50',
    parts: (
      <>
        <path d="M7.5 12h9" />
        <rect x="4.5" y="8" width="3" height="8" rx="1" />
        <rect x="16.5" y="8" width="3" height="8" rx="1" />
        <path d="M2.5 10.5v3M21.5 10.5v3" />
      </>
    ),
  },
  constitution: {
    colour: '#e05a5a',
    parts: <path d="M12 20s-7-4.4-7-9.5A4 4 0 0 1 12 8a4 4 0 0 1 7 2.5C19 15.6 12 20 12 20z" />,
  },
  ranged: {
    colour: '#8bc34a',
    parts: (
      <>
        <path d="M6 4c5 2 8 5 8 8s-3 6-8 8" />
        <path d="M6 4v16" />
        <path d="M3 12h17M17 9l3 3-3 3" />
      </>
    ),
  },
  faith: {
    colour: '#ddb43f',
    parts: (
      <>
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
      </>
    ),
  },
  magic: {
    colour: '#8c96ff',
    parts: (
      <>
        <path d="M4 20 14 10" />
        <path d="M17 3v4M15 5h4" />
        <path d="M20 10v2M19 11h2" />
        <path d="M11 4v2M10 5h2" />
      </>
    ),
  },
  cooking: {
    colour: '#e0954f',
    parts: (
      <>
        <path d="M4 11h16v4a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" />
        <path d="M2 11h2M20 11h2" />
        <path d="M9.5 7.5c0-1 1-1.2 1-2.5M13.5 7.5c0-1 1-1.2 1-2.5" />
      </>
    ),
  },
  woodcutting: {
    colour: '#b58b5a',
    parts: (
      <>
        <path d="M4 20 15.5 8.5" />
        <path d="M14.5 5.5 18 2c2 2 3 4.5 3 7l-3.5 1.5z" />
      </>
    ),
  },
  fishing: {
    colour: '#4fc3f7',
    parts: (
      <>
        <path d="M5 12c2.5-3.5 6-5 9.5-5 3 0 5.5 2 6.5 5-1 3-3.5 5-6.5 5-3.5 0-7-1.5-9.5-5z" />
        <path d="M5 12 2 8.5v7z" />
        <circle cx="16.5" cy="11" r="0.9" />
      </>
    ),
  },
  firemaking: {
    colour: '#ff8a3d',
    parts: (
      <path d="M12 21c-4 0-6-3-6-6 0-4 3-6 4-10 2 2 3 4 3 6 1-1 2-2 2-4 2 2 3 5 3 8 0 3-2 6-6 6z" />
    ),
  },
  crafting: {
    colour: '#26c6da',
    parts: (
      <>
        <path d="M6 4h12l3 5-9 11L3 9z" />
        <path d="M3 9h18M9 4 7.5 9 12 20l4.5-11L15 4" />
      </>
    ),
  },
  smithing: {
    colour: '#b8c4cc',
    parts: (
      <>
        <path d="M4 20 13 11" />
        <path d="M11 7l4-4 6 6-4 4z" />
      </>
    ),
  },
  mining: {
    colour: '#a1887f',
    parts: (
      <>
        <path d="M4 9c4-5 12-5 16 0" />
        <path d="M12 6v15" />
      </>
    ),
  },
  alchemy: {
    colour: '#c07ad6',
    parts: (
      <>
        <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3" />
        <path d="M7.5 15h9" />
      </>
    ),
  },
  agility: {
    colour: '#64b5f6',
    // A feather: light on the feet. A hurdle with a leap over it read, this
    // small, as a telephone.
    parts: (
      <>
        <path d="M6 18C9 11 13 7 20 4c-1 7-5 11-13 13" />
        <path d="M4 20 13 11" />
        <path d="M10.5 13.5h4M13 10.5h4" />
      </>
    ),
  },
  thieving: {
    colour: '#9f86d9',
    parts: (
      <>
        <path d="M3 9c3-1 6-1 9 1 3-2 6-2 9-1 0 4-2 7-5 7-2 0-3-1-4-2-1 1-2 2-4 2-3 0-5-3-5-7z" />
        <circle cx="8" cy="11.5" r="1.2" />
        <circle cx="16" cy="11.5" r="1.2" />
      </>
    ),
  },
  farming: {
    colour: '#66bb6a',
    parts: (
      <>
        <path d="M12 21v-9" />
        <path d="M12 12c0-4 3-6 7-6 0 4-3 6-7 6z" />
        <path d="M12 14c0-3-2-5-6-5 0 3 2 5 6 5z" />
        <path d="M8 21h8" />
      </>
    ),
  },
  hunter: {
    colour: '#cfa76a',
    parts: (
      <>
        <circle cx="12" cy="15.5" r="3.5" />
        <circle cx="6.5" cy="10" r="1.6" />
        <circle cx="10" cy="6.5" r="1.6" />
        <circle cx="14" cy="6.5" r="1.6" />
        <circle cx="17.5" cy="10" r="1.6" />
      </>
    ),
  },
  construction: {
    colour: '#c4ada6',
    parts: (
      <>
        <path d="M3 11 12 4l9 7" />
        <path d="M5 10v10h14V10" />
        <path d="M10 20v-5h4v5" />
      </>
    ),
  },
};

/** The picture for one skill, by name. Nothing for a skill without one. */
export function SkillIcon({ skill }: { skill: string }): JSX.Element | null {
  const icon = ICONS[skill.toLowerCase()];
  if (!icon) return null;

  return (
    <svg
      className="skill-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ color: icon.colour }}
    >
      {icon.parts}
    </svg>
  );
}
